import type React from 'react';

import type { ExtractedContext, ExtractedContextV2, MentionType } from '@aline/common';
import {
  decodeExtractedContext,
  decodeExtractedContextV2,
  decodeSelectionContextV3,
  getIsMentionType,
  mentionType,
} from '@aline/common';
import { ElementApi, TextApi } from 'platejs';
import type { At, Descendant, TElement, TLinkElement } from 'platejs';
import type { PlateEditor } from 'platejs/react';

import { focusEditor } from '../utils';

/**
 * Base interface for all mention elements
 */
interface BaseMentionElement extends TElement {
  type: 'mention';
  value: string;
}

/**
 * Document mention element (IMPLEMENTED)
 * Used for mentioning documents/files in the chat editor
 * Only stores documentId - all other data (icon, processing status) fetched from Apollo cache
 */
export interface DocumentMentionElement extends BaseMentionElement {
  documentId: string;
  documentName: string;
  mentionTypeName: 'document';
  isPlaceholder?: boolean;
}

/**
 * Party mention element (NOT YET IMPLEMENTED)
 * For mentioning parties/entities in the chat
 *
 * TODO: Implement party mention functionality
 * - Add PartyMentionMenuContent component
 * - Create party search/selection UI
 * - Update MentionElement component to handle party rendering
 * - Add tests for party mention serialization/deserialization
 */
export interface PartyMentionElement extends BaseMentionElement {
  mentionTypeName: 'party';
  partyId: string;
  partyName: string;
}

/**
 * Workflow mention element (NOT YET IMPLEMENTED)
 * For mentioning workflows in the chat
 *
 * TODO: Implement workflow mention functionality
 * - Add WorkflowMentionMenuContent component
 * - Create workflow search/selection UI
 * - Update MentionElement component to handle workflow rendering
 * - Add tests for workflow mention serialization/deserialization
 */
export interface WorkflowMentionElement extends BaseMentionElement {
  mentionTypeName: 'workflow';
  workflowId: string;
  workflowName: string;
}

/**
 * Report mention element
 * Used for mentioning reports in the chat editor
 */
export interface ReportMentionElement extends BaseMentionElement {
  mentionTypeName: 'report';
  reportId: string;
  reportName: string;
}

/**
 * Task mention element.
 *
 * Used to reference workflow tasks in the chat editor.
 */
export interface TaskMentionElement extends BaseMentionElement {
  mentionTypeName: 'task';
  taskId: string;
  taskName: string;
}

/**
 * Column mention element
 * Used in AI Reminder Automation prompts to reference specific report columns.
 * Rendered as a pill with the column display name.
 */
export interface ReportColumnMentionElement extends BaseMentionElement {
  columnId: string;
  columnName: string;
  mentionTypeName: 'report-column';
}

/**
 * User mention element.
 *
 * Used to reference a workspace user — typically inside a workflow task
 * description assigned to Aline AI, so the AI can recognize user references
 * (for example to dynamically reassign the task to a specific person based
 * on conditions in the description).
 */
export interface UserMentionElement extends BaseMentionElement {
  mentionTypeName: 'user';
  userId: string;
  userName: string;
}

/** Email mention element for ad-hoc comment recipients outside the workspace. */
export interface EmailMentionElement extends BaseMentionElement {
  email: string;
  mentionTypeName: 'email';
}

/** Contextual mention for the active workflow/task intake form. */
export interface IntakeFormMentionElement extends BaseMentionElement {
  mentionTypeName: 'intake-form';
}

/**
 * Reserved assistant mention used by activity composers to invoke Aline AI.
 * It is a client-only mention that round-trips as `mention:aline-ai`.
 */
export interface AlineAiMentionElement extends BaseMentionElement {
  mentionTypeName: 'aline-ai';
}

/**
 * Playbook mention element.
 *
 * Used to reference an AI Playbook so the chat / AI execution run can hydrate
 * the playbook's title and rules as additional context. Read by the server-side
 * mention hydration when seeding a task execution chat.
 */
export interface PlaybookMentionElement extends BaseMentionElement {
  mentionTypeName: 'playbook';
  playbookId: string;
  playbookName: string;
}

/**
 * Playbook rule mention element: one rule of a playbook, by the rule's id.
 * The playbook it belongs to is resolved by the server, for the chip and for
 * the agent, so the element carries only the rule.
 */
export interface PlaybookRuleMentionElement extends BaseMentionElement {
  mentionTypeName: 'playbook-rule';
  playbookRuleId: string;
  playbookRuleName: string;
}

/**
 * Selection mention element
 * Used when user copies text from Aline Editor and pastes into Chat Editor
 * Preserves full ExtractedContext with paragraphs, selection status, and partial selection handling
 * Only stores documentId - all other data (icon, processing status) fetched from Apollo cache
 */
export interface SelectionMentionElement extends BaseMentionElement {
  documentId: string;
  documentName: string;
  extractedContext: ExtractedContext;
  mentionTypeName: 'selection';
}

/**
 * v2 selection mention element. Same shape as SelectionMentionElement
 * except it carries ExtractedContextV2, which adds focusSuggestionId so the
 * server can correlate the SELECTED part back to a specific suggestion
 * marker (used by "Refine AI Edit"). The serialized paragraph data still
 * uses the same format as v1 / read_document / query_document, so the LLM
 * parser is unchanged.
 */
export interface SelectionV2MentionElement extends BaseMentionElement {
  documentId: string;
  documentName: string;
  extractedContext: ExtractedContextV2;
  mentionTypeName: 'selection-v2';
}

/**
 * v3 selection mention element. Unlike v1/v2 (which carry structured
 * `ExtractedParagraph[]` re-serialized to pipe lines server-side), v3 carries
 * the pre-serialized HTML-like Cast markup produced on the client by
 * `serializeSelectionContext` — including the `<selection-start/>` /
 * `<selection-end/>` span. `focusSuggestionId` correlates the selection back to
 * a suggestion marker (Refine AI Edit). Encoded into the `mention:selection-v3`
 * URL via `encodeSelectionContextV3`.
 */
export interface SelectionV3MentionElement extends BaseMentionElement {
  documentId: string;
  documentName: string;
  focusSuggestionId: string | null;
  html: string;
  mentionTypeName: 'selection-v3';
}

/**
 * Union type for all mention element types
 * Currently only DocumentMentionElement, ReportMentionElement and SelectionMentionElement are fully implemented
 */
export type TMentionElement =
  | AlineAiMentionElement
  | ReportColumnMentionElement
  | DocumentMentionElement
  | EmailMentionElement
  | IntakeFormMentionElement
  | PartyMentionElement
  | PlaybookMentionElement
  | PlaybookRuleMentionElement
  | ReportMentionElement
  | SelectionMentionElement
  | SelectionV2MentionElement
  | SelectionV3MentionElement
  | TaskMentionElement
  | UserMentionElement
  | WorkflowMentionElement;

/**
 * The subset of mention elements that DocumentMentionElement (the React component)
 * renders: a plain document chip and the two selection variants.
 */
export type DocOrSelectionMentionElement = Extract<
  TMentionElement,
  { mentionTypeName: 'document' | 'selection' | 'selection-v2' | 'selection-v3' }
>;

/**
 * Prevents default mouseDown behavior for all elements except INPUT elements.
 * Used in dropdowns/menus to prevent losing focus when clicking on the menu,
 * while still allowing inputs within the menu to be focused.
 *
 * @param e - Mouse event
 *
 * @example
 * ```typescript
 * <div onMouseDown={preventNonInputMouseDown}>
 *   <input /> // This can still be focused
 *   <div>Other content</div> // This won't steal focus
 * </div>
 * ```
 */
export function preventNonInputMouseDown(e: React.MouseEvent): void {
  // Allow inputs to receive focus, prevent default for everything else
  if (e.target instanceof HTMLElement && e.target.tagName !== 'INPUT') {
    e.preventDefault();
  }
}

/**
 * Type guard to check if a node is a mention element.
 * Use this to safely narrow Descendant/TElement to TMentionElement.
 *
 * @param node - Any slate node (Descendant, TElement, etc.)
 * @param mentionType - The mention type string to check for (default: 'mention')
 * @returns True if node is a TMentionElement
 *
 * @example
 * ```typescript
 * for (const node of nodes) {
 *   if (isMentionElement(node)) {
 *     // TypeScript knows node is TMentionElement
 *     console.log(node.mentionTypeName);
 *   }
 * }
 * ```
 */
export function isMentionElement(node: unknown, mentionType = 'mention'): node is TMentionElement {
  return (
    typeof node === 'object' &&
    node !== null &&
    'type' in node &&
    node.type === mentionType &&
    'mentionTypeName' in node
  );
}

/**
 * Type guard to narrow TMentionElement to DocumentMentionElement.
 * Use this instead of type assertions when you need to work with document mentions.
 *
 * @param element - Any mention element
 * @returns True if element is a DocumentMentionElement
 *
 * @example
 * ```typescript
 * if (!isDocumentMentionElement(element)) {
 *   return <div>Unsupported mention type</div>;
 * }
 * // TypeScript now knows element is DocumentMentionElement
 * const docId = element.documentId;
 * ```
 */
export function isDocumentMentionElement(
  element: TMentionElement
): element is DocumentMentionElement {
  return element.mentionTypeName === 'document';
}

/**
 * Inserts a mention node into the editor at the current cursor position.
 * Adds a space after the mention and moves the cursor forward.
 *
 * @param editor - Plate editor instance
 * @param mentionNode - The mention element to insert
 *
 * @example
 * ```typescript
 * insertMentionAt(editor, {
 *   mentionNode: {
 *     type: 'mention',
 *     mentionTypeName: 'document',
 *     documentId: '123',
 *     documentName: 'My Document'
 *   }
 * });
 * ```
 */
export function insertMentionAt(
  editor: PlateEditor,
  options: {
    mentionNode: TMentionElement;
    at?: At;
  }
): void {
  const { at, mentionNode } = options;
  editor.tf.withoutNormalizing(() => {
    // Remove all mention_input elements BEFORE inserting the new mention node.
    editor.tf.removeNodes({ at: [], match: n => n.type === 'mention_input' });
    // Add space so cursor is placed after the mention node with a space.
    editor.tf.insertNodes([mentionNode, { text: ' ' }], { at, select: true });
  });

  focusEditor(editor);
}

/**
 * Type guard to check if a node is a link element with url property.
 * Used during markdown deserialization to identify mention: protocol links.
 *
 * @param node - Element node to check
 * @returns True if node is a link element with a url string
 */
export function isLinkElement(node: TElement): node is TLinkElement {
  return typeof (node as TLinkElement).url === 'string';
}

/**
 * Extracts text content from an array of Descendant children.
 * Used to get the display name from link children during deserialization.
 *
 * @param children - Array of Descendant nodes
 * @returns The text content with @ prefix removed, or empty string
 */
export function getTextFromChildren(children: Descendant[] | undefined): string {
  const firstChild = children?.at(0);
  if (TextApi.isText(firstChild)) return firstChild.text.replace('@', '');

  return '';
}

/**
 * Checks if a Descendant node is an empty text node.
 * Used to filter out empty text nodes during transformation.
 *
 * @param node - Descendant node to check
 * @returns True if node is a text node with empty string
 */
export function isEmptyTextNode(node: Descendant): boolean {
  return TextApi.isText(node) && node.text === '';
}

/**
 * Mention typenames that exist only in the client editor and have no
 * counterpart in the shared MENTION_TYPES wire format. The keyed Record keeps
 * this list compile-time exhaustive: adding a client-only mention element to
 * TMentionElement forces an entry here, and promoting one into @aline/common
 * forces its removal.
 */
type ClientOnlyMentionTypename = Exclude<TMentionElement['mentionTypeName'], MentionType>;

const CLIENT_ONLY_MENTION_TYPENAMES: Record<ClientOnlyMentionTypename, true> = {
  'aline-ai': true,
  'report-column': true,
};

export function getIsMentionTypename(str: string): str is TMentionElement['mentionTypeName'] {
  return getIsMentionType(str) || str in CLIENT_ONLY_MENTION_TYPENAMES;
}

export function getIsMentionElement(element: unknown): element is TMentionElement {
  return ElementApi.isElement(element) && element.type === 'mention';
}

/**
 * Transforms deserialized markdown nodes, converting mention: protocol links
 * back into proper mention elements. This is needed because the MarkdownPlugin
 * serializes mentions as links with mention: protocol, but doesn't automatically
 * deserialize them back.
 *
 * @param node - Descendant node to transform
 * @returns Transformed node (mention element if it was a mention link, otherwise original with transformed children)
 *
 * @example
 * ```typescript
 * const deserialized = editor.api.markdown.deserialize(markdown);
 * const transformed = deserialized.map(transformMentionLinks);
 * ```
 */
export function transformMentionLinks(node: Descendant): Descendant | TMentionElement {
  if (!ElementApi.isElement(node)) return node;

  // If it's a link with mention: protocol, convert to mention
  if (isLinkElement(node) && node.url.startsWith(`${mentionType}:`)) {
    const urlData = node.url.replace(`${mentionType}:`, '');
    const name = getTextFromChildren(node.children);

    /**
     * Split URL into path and query params
     * Format: typename/id or typename/id?ctx=... (for selections)
     */
    const [pathPart, queryPart] = urlData.split('?');
    const [typeName, id] = pathPart.split('/');
    if (!getIsMentionTypename(typeName)) return node;

    // Create mention element with type-specific properties
    switch (typeName) {
      case 'aline-ai':
        return {
          children: [{ text: '' }],
          mentionTypeName: 'aline-ai',
          type: mentionType,
          value: 'aline-ai',
        } satisfies AlineAiMentionElement;
      case 'report-column':
        return {
          children: [{ text: '' }],
          columnId: id,
          columnName: name,
          mentionTypeName: 'report-column',
          type: mentionType,
          value: name,
        } satisfies ReportColumnMentionElement;
      case 'document': {
        return {
          children: [{ text: '' }],
          documentId: id,
          documentName: name,
          mentionTypeName: 'document',
          type: mentionType,
          value: name,
        } satisfies DocumentMentionElement;
      }
      case 'party':
        return {
          children: [{ text: '' }],
          mentionTypeName: 'party',
          partyId: id,
          partyName: name,
          type: mentionType,
          value: name,
        } satisfies PartyMentionElement;
      case 'workflow':
        return {
          children: [{ text: '' }],
          mentionTypeName: 'workflow',
          type: mentionType,
          value: name,
          workflowId: id,
          workflowName: name,
        } satisfies WorkflowMentionElement;
      case 'report':
        return {
          children: [{ text: '' }],
          mentionTypeName: 'report',
          reportId: id,
          reportName: name,
          type: mentionType,
          value: name,
        } satisfies ReportMentionElement;
      case 'task':
        return {
          children: [{ text: '' }],
          mentionTypeName: 'task',
          taskId: id,
          taskName: name,
          type: mentionType,
          value: name,
        } satisfies TaskMentionElement;
      case 'user':
        // A malformed link with no id stays a plain link: a UserMentionElement must carry a real userId.
        if (!id) return node;
        return {
          children: [{ text: '' }],
          mentionTypeName: 'user',
          type: mentionType,
          userId: id,
          userName: name,
          value: name,
        } satisfies UserMentionElement;
      case 'email': {
        const email = decodeMentionEmailId(id);
        return {
          children: [{ text: '' }],
          email,
          mentionTypeName: 'email',
          type: mentionType,
          value: email,
        } satisfies EmailMentionElement;
      }
      case 'intake-form':
        return {
          children: [{ text: '' }],
          mentionTypeName: 'intake-form',
          type: mentionType,
          value: name || 'Intake Form',
        } satisfies IntakeFormMentionElement;
      case 'playbook':
        return {
          children: [{ text: '' }],
          mentionTypeName: 'playbook',
          playbookId: id,
          playbookName: name,
          type: mentionType,
          value: name,
        } satisfies PlaybookMentionElement;
      case 'playbook-rule':
        return {
          children: [{ text: '' }],
          mentionTypeName: 'playbook-rule',
          playbookRuleId: id,
          playbookRuleName: name,
          type: mentionType,
          value: name,
        } satisfies PlaybookRuleMentionElement;
      case 'selection': {
        const queryParams = new URLSearchParams(queryPart);
        const encodedContext = queryParams.get('ctx');

        if (!encodedContext) {
          console.warn('Selection mention missing encodedContext metadata');
          return node;
        }

        const extractedContext = decodeExtractedContext(encodedContext);
        if (!extractedContext) {
          console.warn('Failed to decode selection context probably due to legacy format');
          // Return empty text node which gets filtered out, showing nothing
          return { text: '' };
        }

        return {
          children: [{ text: '' }],
          documentId: id,
          documentName: name,
          extractedContext,
          mentionTypeName: 'selection',
          type: mentionType,
          value: name,
        } satisfies SelectionMentionElement;
      }
      case 'selection-v2': {
        const queryParams = new URLSearchParams(queryPart);
        const encodedContext = queryParams.get('ctx');

        if (!encodedContext) {
          console.warn('Selection-v2 mention missing encodedContext metadata');
          return node;
        }

        const extractedContext = decodeExtractedContextV2(encodedContext);
        if (!extractedContext) {
          console.warn('Failed to decode selection-v2 context');
          return { text: '' };
        }

        return {
          children: [{ text: '' }],
          documentId: id,
          documentName: name,
          extractedContext,
          mentionTypeName: 'selection-v2',
          type: mentionType,
          value: name,
        } satisfies SelectionV2MentionElement;
      }
      case 'selection-v3': {
        const queryParams = new URLSearchParams(queryPart);
        const encodedContext = queryParams.get('ctx');

        if (!encodedContext) {
          console.warn('Selection-v3 mention missing encodedContext metadata');
          return node;
        }

        const context = decodeSelectionContextV3(encodedContext);
        if (!context) {
          console.warn('Failed to decode selection-v3 context');
          return { text: '' };
        }

        return {
          children: [{ text: '' }],
          documentId: id,
          documentName: name,
          focusSuggestionId: context.focusSuggestionId,
          html: context.html,
          mentionTypeName: 'selection-v3',
          type: mentionType,
          value: name,
        } satisfies SelectionV3MentionElement;
      }
    }
  }

  // Recursively transform children
  if (Array.isArray(node.children)) {
    const transformedChildren = node.children
      .map(transformMentionLinks)
      .filter(child => !isEmptyTextNode(child));

    return {
      ...node,
      children: transformedChildren.length > 0 ? transformedChildren : [{ text: '' }],
    };
  }

  return node;
}

function decodeMentionEmailId(id: string) {
  try {
    return decodeURIComponent(id);
  } catch {
    return id;
  }
}
